import React, {useState} from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import {Alert, Stack} from '@mui/material';
import {useAuthContext} from "../../auth/useAuthContext";
import ProcessingDialog from "../../components/ProcessingDialog";
import {SupportContact} from "./SupportContact";
import Image from "../../components/image";
import Joyride from 'react-joyride';

const Visibility = () => (
    <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
        <path d="M0 0h24v24H0z" fill="none"/>
        <path
            d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
    </svg>
);

const VisibilityOff = () => (
    <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
        <path d="M0 0h24v24H0z" fill="none"/>
        <path
            d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
    </svg>
);

export const AuthLoginForm = ({onLoginSuccess}) => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const {login} = useAuthContext();

    const [runTutorial, setRunTutorial] = useState(false);

    const joyrideSteps = [
        {
            target: '.email-field',
            content: 'Ovde unesite vašu mejl adresu.',
            disableBeacon: true
        },
        {
            target: '.password-field',
            content: 'Ovde unesite vašu lozinku.'
        },
        {
            target: '.login-button',
            content: 'Kliknite ovde da se prijavite.'
        }
    ];

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError('');

        if (!email.trim()) {
            setError('Mejl je obavezan!');
            setLoading(false);
            return;
        }
        if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
            setError('Mejl adresa mora biti validnog formata!');
            setLoading(false);
            return;
        }
        if (!password) {
            setError('Lozinka je obavezna!');
            setLoading(false);
            return;
        }

        try {
            await login(email.trim(), password.trim());
            onLoginSuccess();
            setLoading(false);
        } catch (error) {
            setError('Uneti kredencijali nisu validni!');
            setLoading(false);
        }
    };

    if (loading) {
        return <ProcessingDialog loading={loading}/>;
    }

    return (
        <Box sx={{
            mx: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
        }}>
            <Joyride
                steps={joyrideSteps}
                run={runTutorial}
                continuous
                showProgress
                showSkipButton
                locale={{
                    back: 'Nazad',
                    close: 'Zatvori',
                    last: 'Završi',
                    next: 'Dalje',
                    skip: 'Preskoči',
                    tooltip: {
                        step: (current, total) => `Korak ${current} od ${total}`
                    }
                }}
                styles={{ options: { zIndex: 1300, primaryColor: '#1976d2' } }}
                callback={(data) => {
                    if (data.status === 'finished' || data.status === 'skipped') {
                        setRunTutorial(false);
                    }
                }}
            />

            <Image
                src="/assets/bus.png"
                disabledEffect
                alt="pin"
                contentSx={{height: 350, objectFit: "contain"}}
            />

            <Box component="form" noValidate onSubmit={handleSubmit} sx={{mt: 1, width: '100%'}}>
                <Stack spacing={2}>
                    {!!error && <Alert severity="warning">{error}</Alert>}

                    <TextField
                        name="email"
                        label="Mejl"
                        required
                        fullWidth
                        className="email-field"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoFocus
                    />

                    <TextField
                        name="password"
                        label="Lozinka"
                        type={showPassword ? 'text' : 'password'}
                        required
                        fullWidth
                        className="password-field"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                        {showPassword ? <VisibilityOff/> : <Visibility/>}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </Stack>

                <Button
                    fullWidth
                    color="inherit"
                    size="large"
                    type="submit"
                    variant="contained"
                    className="login-button"
                    sx={{
                        mt: 3,
                        py: 1.5,
                        bgcolor: 'text.primary',
                        color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
                        '&:hover': {
                            bgcolor: 'text.primary',
                            color: (theme) => (theme.palette.mode === 'light' ? 'common.white' : 'grey.800'),
                        },
                    }}
                >
                    Prijavi se
                </Button>

                <Button
                    onClick={() => setRunTutorial(true)}
                    variant="text"
                    size="small"
                    sx={{ mt: 1 }}
                >
                    Pomoć (Prikaži uputstvo)
                </Button>
            </Box>

            <SupportContact/>
        </Box>
    );
};
